import { useRef, useState } from 'react';
import {
  Card,
  Badge,
  Button,
  LoadingSkeleton,
  useToast,
} from '@nexora/design-system';
import {
  useImports,
  useUploadImport,
  useProcessImport,
  type Import,
} from './useIntegration';

/* ── Helpers ── */

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const statusVariant: Record<string, 'info' | 'success' | 'error' | 'neutral'> = {
  uploaded: 'info',
  validating: 'info',
  validated: 'info',
  pending: 'info',
  processing: 'info',
  completed: 'success',
  failed: 'error',
};

const statusLabel: Record<string, string> = {
  uploaded: 'Enviado',
  validating: 'Validando',
  validated: 'Validado',
  pending: 'Pendente',
  processing: 'Processando',
  completed: 'Concluido',
  failed: 'Falhou',
};

/* ── Main Page ── */

export function IntegrationImportsPage(): JSX.Element {
  const { addToast } = useToast();
  const { data: imports, isLoading, error } = useImports();
  const uploadMutation = useUploadImport();
  const processMutation = useProcessImport();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const handleFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      await uploadMutation.mutateAsync(file);
      addToast('Importação enviada com sucesso', 'success');
    } catch (err) {
      addToast(
        (err as Error).message ?? 'Erro ao enviar arquivo',
        'error',
      );
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleProcess = (id: string): void => {
    processMutation.mutate(id, {
      onSuccess: () => {
        addToast('Processamento iniciado', 'success');
      },
      onError: (err) => {
        addToast(
          (err as Error).message ?? 'Erro ao processar importacao',
          'error',
        );
      },
    });
  };

  return (
    <div className="integration-page">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
                  <h1 className="font-display-lg text-display-lg text-on-surface m-0">Importações</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">Importações de dados e seu status.</p>
        </div>
        <div className="flex flex-wrap items-center gap-sm">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx,.xml,.json"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? 'Enviando...' : 'Importar Arquivo'}
          </Button>
        </div>
      </div>

      <Card>
        {isLoading ? (
          <div
            style={{
              padding: '1rem',
              display: 'flex',
              flexDirection: 'column',
              gap: '0.75rem',
            }}
          >
            <LoadingSkeleton count={4} height="48px" />
          </div>
        ) : error ? (
          <div className="state-message error">
            <p>Erro ao carregar importacoes.</p>
          </div>
        ) : !imports || imports.length === 0 ? (
          <div className="state-message empty">
            <p>Nenhuma importacao encontrada.</p>
            <p style={{ fontSize: '0.8125rem', color: '#74777f' }}>
              Clique em &quot;Importar Arquivo&quot; para iniciar uma
              importacao.
            </p>
          </div>
        ) : (
          <div className="table-wrapper">
              <div className="table-header">
                <span className="col-file">Arquivo</span>
                <span className="col-size">Linhas</span>
              <span className="col-type">Tipo</span>
              <span className="col-status">Status</span>
              <span className="col-errors">Erros</span>
              <span className="col-date">Data</span>
              <span className="col-actions">Ações</span>
            </div>
            {imports.map((imp: Import) => (
              <div key={imp.id} className="table-row">
                <span className="col-file" title={imp.fileName}>
                  {imp.fileName}
                </span>
                <span className="col-size">
                  {imp.totalRows}
                </span>
                <span className="col-type">{imp.type}</span>
                <span className="col-status">
                  <Badge variant={statusVariant[imp.status] ?? 'neutral'}>
                    {statusLabel[imp.status] ?? imp.status}
                  </Badge>
                </span>
                <span className="col-errors">
                  {imp.errors.length > 0 ? (
                    <span
                      className="errors-trigger"
                      title={imp.errors.join('\n')}
                    >
                      {imp.errors.length} erro(s)
                    </span>
                  ) : (
                    '-'
                  )}
                </span>
                <span className="col-date">{formatDate(imp.createdAt)}</span>
                <span className="col-actions">
                  {imp.status === 'validated' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleProcess(imp.id)}
                      disabled={processMutation.isPending}
                    >
                      Processar
                    </Button>
                  )}
                </span>
              </div>
            ))}
          </div>
        )}
      </Card>

      <style>{`
        .integration-page { padding: 1.5rem; }

        .state-message {
          padding: 2rem;
          text-align: center;
        }
        .state-message p { margin: 0.25rem 0; font-size: 0.875rem; color: #43474e; }
        .state-message.error p { color: #ba1a1a; }

        .table-wrapper {
          font-size: 0.8125rem;
        }
        .table-header,
        .table-row {
          display: grid;
          grid-template-columns: 1.5fr 90px 90px 110px 80px 130px 100px;
          gap: 1rem;
          padding: 0.75rem 1rem;
          align-items: center;
        }
        .table-header {
          background: #f2f4f6;
          font-weight: 600;
          color: #43474e;
          border-bottom: 1px solid #e0e3e5;
        }
        .table-row {
          border-bottom: 1px solid #eceef0;
          transition: background 0.15s;
        }
        .table-row:hover {
          background: #f2f4f6;
        }
        .col-file {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .col-actions {
          display: flex;
          gap: 0.5rem;
        }
        .errors-trigger {
          color: #ba1a1a;
          cursor: help;
          border-bottom: 1px dashed #ba1a1a;
        }
      `}</style>
    </div>
  );
}
